import React, { useState } from 'react';
import { Modal, Input, Space, Button, message } from 'antd';

const { TextArea } = Input;

interface SuspendPendingModalProps {
  open: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  loading?: boolean;
}

const SUSPEND_REASONS = [
  {
    value: '空号/未接',
    label: '空号/未接',
    desc: '无法联系到客户',
  },
  {
    value: '待微信沟通',
    label: '待微信沟通',
    desc: '需要通过微信进一步沟通',
  },
  {
    value: '其他',
    label: '其他',
    desc: '其他原因需要说明',
  },
];

const getCardStyle = (isSelected: boolean): React.CSSProperties => ({
  border: `2px solid ${isSelected ? '#2b7fff' : '#e5e7eb'}`,
  backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
  borderRadius: 10,
  padding: '16px 18px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
});

const getRadioCircleStyle = (isSelected: boolean): React.CSSProperties => ({
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: `2px solid ${isSelected ? '#2b7fff' : '#d1d5dc'}`,
  backgroundColor: isSelected ? '#2b7fff' : '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 2,
});

const SuspendPendingModal: React.FC<SuspendPendingModalProps> = ({
  open,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [detailReason, setDetailReason] = useState<string>('');

  const handleConfirm = () => {
    if (!selectedReason) {
      message.warning('请选择暂存原因');
      return;
    }

    let finalReason;
    if (selectedReason === '其他') {
      if (!detailReason.trim()) {
        message.warning('请输入具体说明');
        return;
      }
      finalReason = detailReason.trim();
    } else {
      finalReason = selectedReason;
    }

    onConfirm(finalReason);
  };

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (open) {
      setSelectedReason('');
      setDetailReason('');
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={520}
      title={
        <div style={{ paddingBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 600, color: '#101828', marginBottom: 4 }}>暂存待办</div>
          <div style={{ fontSize: 14, color: '#6a7282', fontWeight: 400 }}>请选择暂存原因，以便后续跟进</div>
        </div>
      }
      styles={{
        header: {
          borderBottom: '1px solid #f3f4f6',
          marginBottom: 16,
          padding: '24px 24px 0 24px',
        },
        body: { padding: '0 24px 24px 24px' },
        footer: {
          padding: '16px 24px',
          background: '#f9fafb',
          borderTop: '1px solid #f3f4f6',
          margin: 0,
          borderRadius: '0 0 8px 8px'
        }
      }}
      closeIcon={null}
      footer={
        <Space size={12}>
          <Button onClick={onCancel} style={{ borderRadius: 8, height: 36, padding: '0 16px' }}>
            取消
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            loading={loading}
            style={{ borderRadius: 8, height: 36, padding: '0 24px', background: '#155dfc' }}
          >
            确认暂存
          </Button>
        </Space>
      }
    >
      <div style={{ marginBottom: 12, fontSize: 14, fontWeight: 500, color: '#364153' }}>
        暂存原因
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
        {SUSPEND_REASONS.map((reason) => {
          const isSelected = selectedReason === reason.value;
          return (
            <div
              key={reason.value}
              style={getCardStyle(isSelected)}
              onClick={() => setSelectedReason(reason.value)}
            >
              <div style={getRadioCircleStyle(isSelected)}>
                {isSelected && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <div>
                <div style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: isSelected ? '#1c398e' : '#101828',
                  lineHeight: '24px',
                  marginBottom: 2
                }}>
                  {reason.label}
                </div>
                <div style={{
                  fontSize: 14,
                  color: isSelected ? '#1447e6' : '#6a7282',
                  lineHeight: '20px'
                }}>
                  {reason.desc}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selectedReason === '其他' && (
        <TextArea
          placeholder="请详细说明暂存原因，以便后续处理..."
          value={detailReason}
          onChange={(e) => setDetailReason(e.target.value)}
          autoSize={{ minRows: 3, maxRows: 6 }}
          style={{
            background: '#f3f3f5',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 14,
            color: '#364153'
          }}
        />
      )}
    </Modal>
  );
};

export default SuspendPendingModal;
